import { motion } from 'framer-motion';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { AddIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { IconButton, Modal, Popover } from '@treams/treams-ui';

import { FeedbackLibrary } from './FeedbackLibrary';

const PlaceholderContainer = styled(Flex)`
	border: 1px dashed ${(props) => props.theme.tokens.border.default};
	min-height: 64px;
	border-radius: ${(props) => props.theme.tokens.radius.md}px;
	cursor: pointer;
	background: ${(props) => props.theme.tokens.surface.white};
	transition:
		0.2s ease-in-out border-color,
		0.2s ease-in-out background-color;

	&:hover {
		background: ${(props) => props.theme.tokens.surface.hover};

		&:active {
			background: ${(props) => props.theme.tokens.surface.secondary};
		}
	}
`;

function QuestionSelector(props: {
	readOnly?: boolean;
	onCancel?: () => void;
	onSelect?: (questions: graphql.FeedbackQuestion[]) => void;
}) {
	const { t } = useTranslation();
	const [selected, setSelected] = useState<graphql.FeedbackQuestion[]>([]);

	return (
		<Modal width={900}>
			<Modal.Header title={t('feedback.settings.template.questionSelector.title')} />
			<Modal.Contents>
				<FeedbackLibrary
					inline
					readOnly={props.readOnly}
					selectedQuestions={selected}
					onChangeSelectedQuestions={setSelected}
				/>
			</Modal.Contents>
			<Modal.FormFooter
				onCancel={props.onCancel}
				onSave={() => props.onSelect?.(_.sortBy(selected, 'order'))}
				saveTitle={t('feedback.settings.template.questionSelector.add')}
			/>
		</Modal>
	);
}

export default function AddFeedbackQuestion(props: {
	onSelect?: (questions: graphql.FeedbackQuestion[]) => void;
}) {
	const [visible, setVisible] = useState(false);

	return (
		<motion.div
			layout="position"
			layoutId="template-question-placeholder"
			transition={{ duration: 0.2, ease: 'circOut' }}
		>
			<Popover.Target
				center
				visible={visible}
				onDismiss={() => setVisible(false)}
				contents={
					<QuestionSelector
						onCancel={() => setVisible(false)}
						onSelect={(questions) => {
							props.onSelect?.(questions);
							setVisible(false);
						}}
					/>
				}
			>
				<PlaceholderContainer
					alignItems="center"
					justifyContent="center"
					ml={80}
					onClick={() => setVisible(true)}
				>
					<IconButton type="secondary" icon={<AddIcon />} />
				</PlaceholderContainer>
			</Popover.Target>
		</motion.div>
	);
}
